import journalEntryValidation from 'domain/finance/journalEntry/createupdate/journalEntryValidation';
import journalEntryFilterValidation from 'domain/finance/journalEntry/List/journalEntryFilterValidation';
import { useFinanceApi } from 'infrastructure/api';
import { useCrudService } from 'framework/crud';
import { storageService, useSingletonPromise, downloadFile } from 'framework/utils';
import useJournalEntriesSettings from 'domain/finance/journalEntry/shared/Settings/useJournalEntriesSettings';
import { useEmployeeService, useEmployeeNamesService } from 'domain/shared/employee';
import { CONTENT_TYPE } from 'framework/api';
import { FormFieldsData } from 'framework/form';
import moment from 'moment';

export const READ_JOURNAL_ENTRY_LIST = 'COMPANY_READ_USER_LEDGER';
export const READ_JOURNAL_ENTRY = 'COMPANY_READ_USER_LEDGER';
export const CREATE_JOURNAL_ENTRY = 'COMPANY_POST_LEDGER';
const RESOURCE_URL = '/ledger/journal-entries';
const JOURNAL_ENTRIES_REPORT_URL = '/reporting/journal-entries/csv';
const PAYROLL_ACCOUNT_ID = 'Expenses/Payroll/USD';
const ACCRUALS_ACCOUNT = 'Expenses/Employee accruals/USD';

const STORAGE_KEY = 'exchange-rates';
const VERSION = '1.1';

export const officeRequiredAccounts = [
  'Assets/Tangible assets/Office facilities',
  'Expenses/Global process operations/Individual equipment',
];
export const employeeRequiredAccounts = [
  'Liabilities/Payroll',
  'Expenses/Payroll',
  'Expenses/Finance and legal/Payroll',
];
export const employeeNotRequiredAccounts = ['Liabilities/Payroll/Employee expenses/Company payroll liabilities'];
export const projectRequiredAccounts = ['Expenses/Delivery'];
export const nonCommercialProjectRequiredAccounts = ['Expenses/Sales and marketing/Marketing'];

export const isRequiredField = (transfers: any[], requiredAccounts: string[]) =>
  transfers.some(
    (transfer: any) => transfer.accountId && requiredAccounts.some(account => transfer.accountId?.startsWith(account)),
  );

const useJournalEntryService = () => {
  const { areStornedJournalEntriesVisible } = useJournalEntriesSettings();
  const { sendPostRequest, sendGetRequest } = useFinanceApi();
  const { getFullName } = useEmployeeService();
  const { getEmployeeNameDataById } = useEmployeeNamesService();

  const { search: initialSearch, getById: initialGetById } = useCrudService({
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: useFinanceApi,
  });

  const { getDataFromStorage, setDataToStorage } = storageService(STORAGE_KEY, () => ({}), VERSION);

  const search = useSingletonPromise('search-ledger-accounts', async ({ filter = {} }: any = {}) => {
    let result = await initialSearch({
      ...filter,
      period: {
        fromDate: filter?.period?.ge,
        toDate: filter?.period?.le,
      },
      employeeAlias: filter?.employeeAlias?.eq,
      accountsIds: filter?.accountsIds,
      postedBy: filter?.postedBy?.eq,
      offices: filter?.offices?.in,
      includeTransfers: !!filter?.includeTransfers,
    });

    if (!areStornedJournalEntriesVisible) {
      const stornedEntriesIds = result.reduce((acc: string[], item: any) => {
        if (item.revertedOriginalJournalEntryId !== null) {
          acc.push(item.revertedOriginalJournalEntryId);
        }
        return acc;
      }, []);

      result = result.filter(
        (item: any) => item.revertedOriginalJournalEntryId === null && !stornedEntriesIds.includes(item.id),
      );
    }

    return {
      result,
      totalElements: result.length,
      totalPages: 1,
    };
  });

  const searchTransfers = async (params: any) => {
    const journalEntries = await search(params);

    const result = journalEntries.result.flatMap(
      ({ transfers, description }: { description: string; transfers: { accountId: string }[] }) => {
        if (params?.filter?.accountsIds || params?.filter?.businessObjectType) {
          return transfers.reduce((acc: any[], transfer: any) => {
            if (params.filter?.accountsIds?.includes(transfer.accountId) || params.filter?.businessObjectType) {
              acc.push({ ...transfer, description: transfer.description || description });
            }

            return acc;
          }, []);
        }

        return transfers;
      },
    );

    return {
      result,
      totalElements: result.length,
      totalPages: 1,
    };
  };

  const formatJournalEntry = async (journalEntry: Dictionary<any>) => {
    let createdByName = null;
    let employeeName = null;

    try {
      createdByName = await getEmployeeNameDataById(journalEntry.postedBy);

      if (journalEntry?.attributes?.employeeAlias) {
        employeeName = await getEmployeeNameDataById(journalEntry.attributes.employeeAlias);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    // TODO add project name from suggest request after updating on BE
    return {
      id: journalEntry.id,
      asDateOf: journalEntry.asDateOf,
      totals: 0,
      description: journalEntry.description,
      transfers: journalEntry.transfers,
      attributes: {
        ...(journalEntry.attributes || {}),
        employeeName: employeeName ? getFullName(employeeName.nameEn) : '',
      },
      createdAt: journalEntry.postedAt,
      createdByName: createdByName ? getFullName(createdByName.nameEn) : '',
    };
  };

  const mappingRequestData = ({
    description,
    asDateOf,
    transfers,
    attributes = {},
    project = {},
    projectName,
  }: Dictionary<any>) => {
    const removeEmployeeNameFromAttributes = (rawAttributes: Dictionary<any> = {}) => {
      const { employeeName: _employeeName, ...sanitizedAttributes } = rawAttributes;
      return sanitizedAttributes;
    };
    const jeAttributes = removeEmployeeNameFromAttributes(attributes);

    let projectAttributes = { ...project };
    let projectNameAttributes = {};

    if (typeof projectName === 'string') {
      projectNameAttributes = { projectName };
    }

    if (projectAttributes.projectId === 'no-specific-project') {
      projectAttributes = {};
    }

    const hasPayrollAccount = transfers.some(
      ({ accountId }: { accountId: string }) => accountId === 'Expenses/Payroll/USD',
    );
    const updatedTransfers = transfers.map(
      ({ accountId, debit, credit, attributes: transferAttributes = {}, description: transferDescription }: any) => {
        const sanitizedTransferAttributes = removeEmployeeNameFromAttributes(transferAttributes);
        const validTransferAttributes = Object.fromEntries(
          Object.entries(sanitizedTransferAttributes).filter(([_, value]) => value !== null && value !== undefined),
        );

        return {
          accountId,
          attributes: {
            ...validTransferAttributes,
            ...projectAttributes,
            ...jeAttributes,
            ...projectNameAttributes,
            ...(hasPayrollAccount ? { businessObjectType: 'PAYROLL' } : {}),
          },
          description: transferDescription,
          debit: debit || 0,
          credit: credit || 0,
          asDateOf,
        };
      },
    );

    return {
      description,
      asDateOf,
      transfers: updatedTransfers,
      attributes: {
        ...jeAttributes,
        ...projectAttributes,
        ...projectNameAttributes,
        ...(hasPayrollAccount ? { businessObjectType: 'PAYROLL' } : {}),
      },
    };
  };

  const getById = async (id: string) => {
    const journalEntry = await initialGetById(id);

    return formatJournalEntry(journalEntry);
  };

  const getJournalEntryByTransferId = async (transferId: string) => {
    const response = await sendGetRequest(`${RESOURCE_URL}/by-transfer/${transferId}`);
    const journalEntry = await response.json();

    return formatJournalEntry(journalEntry);
  };

  const getReversedEntryData = async (id: string) => {
    const data = await getById(id);

    return {
      ...data,
      description: `Storno: ${data.description}`,
      transfers: data.transfers.map((item: any) => ({
        ...item,
        credit: item.debit,
        debit: item.credit,
      })),
    };
  };

  const create = async (data: any) => {
    const dataForRequest = mappingRequestData(data);
    await sendPostRequest(RESOURCE_URL, dataForRequest);
    return {};
  };

  const revokeJournalEntry = async (id: StringOrNumber) => {
    await sendPostRequest(`${RESOURCE_URL}/${id}/storno`);
    return {};
  };

  const update = async (id: StringOrNumber, data: FormFieldsData) => {
    await revokeJournalEntry(id);
    await create(data);
    return {};
  };

  const getExchangeRates = async (date: string) => {
    try {
      const requestedDate = date.trim();
      const savedData = await getDataFromStorage();

      if (savedData[requestedDate]) {
        return savedData[requestedDate];
      }

      const response = await sendGetRequest(`/ledger/exchange-rates?date=${requestedDate}`);
      const result = await response.json();

      setDataToStorage({ ...savedData, [requestedDate]: result });

      return result;
    } catch (e) {
      return {};
    }
  };

  const uploadJournalEntry = async (data: FormData) => {
    const res = await sendPostRequest(`/expenses/upload`, data, { contentType: CONTENT_TYPE.MULTIPART });
    return res.json();
  };

  const submitListOfJournalEntry = async (data: any[]) => {
    return sendPostRequest(`${RESOURCE_URL}/multiple`, { journalEntries: data });
  };

  const downloadJournalEntriesCsvReport = async (period: string) => {
    const periodMoment = moment(period, 'YYYY-MM', true);
    const response = await sendPostRequest(JOURNAL_ENTRIES_REPORT_URL, {
      period: {
        fromDate: periodMoment.clone().startOf('month').format('YYYY-MM-DD'),
        toDate: periodMoment.clone().endOf('month').format('YYYY-MM-DD'),
      },
      accountsIds: [PAYROLL_ACCOUNT_ID, ACCRUALS_ACCOUNT],
      includeTransfers: true,
      excludeDistribution: true,
    });

    return downloadFile(response, decodeURI('journal-entries-report.csv'), true);
  };

  const getValidationSchema = () => Promise.resolve(journalEntryValidation);

  const getFilterValidationSchema = () => Promise.resolve(journalEntryFilterValidation);

  return {
    update,
    search,
    getById,
    create,
    getValidationSchema,
    getFilterValidationSchema,
    getExchangeRates,
    getReversedEntryData,
    searchTransfers,
    revokeJournalEntry,
    getJournalEntryByTransferId,
    uploadJournalEntry,
    submitListOfJournalEntry,
    downloadJournalEntriesCsvReport,
    mappingRequestData,
    formatJournalEntry,
  };
};

export default useJournalEntryService;
